import React, { useMemo, useEffect, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import { Chat as PrivateChat } from "../Chat/Index";
import ChatList from './components/ChatList';
import EmptyState from './components/EmptyState';
import WelcomeState from './components/WelcomeState';
import { Button } from 'primereact/button';
import { useFeedData } from '../../../hooks/useFeedData';
import BusinessFeeds from '../../../components/BusinessFeeds/BusinessFeeds';
import {
  CHAT_MOBILE_VIEW_CLASS,
  CHAT_LAYOUT_CLASS,
  CHAT_MAIN_CLASS,
  CHAT_LIST_ITEM_CLASS
} from './constant';
import "./style.css";
import { useState } from 'react';

// Add new constant for feeds section
export const FEEDS_CLASS = "feeds-section";

const DashboardView = ({
  userRecord,
  isMobile,
  selectedChat,
  activeChat,
  toast,
  guestChats,
  unreadMessages,
  markChatAsRead,
  handleBackToChatList,
  handleChatSelect,
  isLoading,
  error
}) => {

  const { 
    posts, 
    loading: feedLoading, 
    error: feedError, 
    hasMore, 
    loadMorePosts, 
    refreshPosts, 
    fetchPosts, 
    hasFetched
  } = useFeedData([]);

  // Memoize the fetchPosts function to prevent unnecessary re-renders
  const memoizedFetchPosts = useCallback(() => {
    if (!hasFetched) {
      fetchPosts();
    }
  }, [fetchPosts, hasFetched]);

  // Fetch posts when component mounts
  useEffect(() => {
    memoizedFetchPosts();
  }, [memoizedFetchPosts]);

  // Memoize the BusinessFeeds component
  const memoizedBusinessFeeds = useMemo(() => (
    <BusinessFeeds 
      posts={posts} 
      loading={feedLoading} 
      error={feedError} 
      hasMore={hasMore} 
      loadMore={loadMorePosts}
      onRefresh={refreshPosts}
      onLoadMore={loadMorePosts}
      currentUser={userRecord}
      shopDetails={userRecord}
    />
  ), [posts, feedLoading, feedError, hasMore, loadMorePosts, refreshPosts, userRecord]);

  // Memoize the main content to prevent unnecessary re-renders
  const mainContent = useMemo(() => {
    // Mobile view: just show chat or list, no header/menu (handled by layout)
    const renderMobileView = () => {
      if (selectedChat) {
        return (
          <div className={CHAT_MOBILE_VIEW_CLASS}>
            <PrivateChat
              guestId={selectedChat}
              user={userRecord}
              userId={userRecord?.uid}
              shopDetails={userRecord}
              isOwner={true}
              showNameInput={false}
              onBack={handleBackToChatList}
            />
          </div>
        );
      }
      if (guestChats === null) {
        return null;
      }
      return guestChats.length > 0 ? (
        <ChatList
          chats={guestChats}
          unreadMessages={unreadMessages} 
          activeChat={activeChat}
          onChatSelect={handleChatSelect}
          markChatAsRead={markChatAsRead}
        />
      ) : (
        <EmptyState />
      );
    };

    const renderDesktopView = () => {
      if (guestChats === null) {
        return null;
      }
      return (
        <div className="flex flex-row h-full">
          <div className="flex-1">
            {guestChats.length > 0 ? (
              <div className={CHAT_LAYOUT_CLASS}>
                <div className={CHAT_MAIN_CLASS}>
                  {selectedChat ? (
                    <div className={CHAT_MOBILE_VIEW_CLASS}>
                      <PrivateChat
                        guestId={selectedChat}
                        user={userRecord}
                        userId={userRecord?.uid}
                        shopDetails={userRecord}
                        isOwner={true}
                        showNameInput={false}
                      />
                    </div>
                  ) : (
                    <div className="w-full h-full flex flex-column">
                        {memoizedBusinessFeeds}
                      </div>
                  )}
                </div>
                  <ChatList
                    chats={guestChats}
                    unreadMessages={unreadMessages}
                    activeChat={activeChat}
                    onChatSelect={handleChatSelect}
                    markChatAsRead={markChatAsRead}
                  />
              </div>
            ) : (
              <EmptyState />
            )}
          </div>
        </div>
      );
    };

    // Show loading state
    if (isLoading) {
      return (
        <div className="flex align-items-center justify-content-center" style={{ height: '50vh' }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
          <span className="ml-2">Loading your chats...</span>
        </div>
      );
    }

    // Show error state
    if (error) {
      return (
        <div className="p-4">
          <div className="p-4 border-round border-1 border-300 bg-red-100">
            <div className="flex align-items-center">
              <i className="pi pi-exclamation-triangle text-red-500 text-xl mr-2"></i>
              <h4 className="m-0">Error Loading Chats</h4>
            </div>
            <p className="mt-2 mb-0">We couldn't load your chat data. Please try refreshing the page.</p>
            <p className="text-sm text-500 mt-2">Error: {error}</p>
            <Button
              label="Refresh Page"
              icon="pi pi-refresh"
              className="p-button-text p-button-sm"
              onClick={() => window.location.reload()}
            />
          </div>
        </div>
      );
    }

    // Show empty state if no chats
    if (!guestChats || guestChats.length === 0) {
      return (
        <div className="p-4">
          <Toast ref={toast} />
          <EmptyState />
        </div>
      );
    }

    return (
      <div className="dashboard-container">
        <Toast ref={toast} />
        {isMobile ? renderMobileView() : renderDesktopView()}
      </div>
    );
  }, [
    userRecord, 
    isMobile, 
    toast, 
    guestChats, 
    selectedChat, 
    activeChat, 
    markChatAsRead, 
    handleBackToChatList, 
    handleChatSelect, 
    isLoading, 
    unreadMessages, 
    error, 
    posts, 
    feedLoading, 
    feedError, 
    hasMore, 
    loadMorePosts, 
    refreshPosts
    ]);

  return mainContent;
};

export default DashboardView;
